import type { ImageLoaderProps } from "next/image";

interface CloudflareImageLoaderProps {
  src?: string;
  width?: number;
  height?: number;
  quality?: number;
  format?: string;
  fit?: string;
  gravity?: string;
  blur?: number;
  dpr?: number;
  aspectRatio?: string;
}

const wpImagesStorageUrl =
  process.env.NEXT_PUBLIC_WP_IMAGES_STORAGE_URL ?? "content.sifted.eu";
const wpImagesLoaderUrl =
  process.env.NEXT_PUBLIC_WP_IMAGES_LOADER_URL ?? "images.sifted.eu";
const datoImagesStorageUrl =
  process.env.NEXT_PUBLIC_DATO_IMAGES_STORAGE_URL ?? "www.datocms-assets.com";
const datoImagesLoaderUrl =
  process.env.NEXT_PUBLIC_DATO_IMAGES_LOADER_URL ?? "images-datocms.sifted.eu";

const buildHostnameChecker =
  (hostname: string) =>
  (url: string | URL): boolean => {
    if (typeof url === "string") url = new URL(url);
    return url?.hostname === hostname;
  };

export const isWpContentUrl = buildHostnameChecker(wpImagesStorageUrl);
export const isWpCloudflareUrl = buildHostnameChecker(wpImagesLoaderUrl);
export const isDatoContentUrl = buildHostnameChecker(datoImagesStorageUrl);
export const isDatoCloudflareUrl = buildHostnameChecker(datoImagesLoaderUrl);

const storageUrlToLoaderUrl = ({ imageUrl }: { imageUrl: string }): string => {
  if (!imageUrl) return "";
  const url = new URL(imageUrl);
  if (isWpContentUrl(url)) {
    url.hostname = wpImagesLoaderUrl;
  } else if (isDatoContentUrl(url)) {
    url.hostname = datoImagesLoaderUrl;
  } else {
    throw new Error(
      `Incompatible hostname ('${url.hostname}'). Expected '${wpImagesStorageUrl}' or '${datoImagesStorageUrl}'.`
    );
  }

  return url.toString();
};

export const applyAspectRatio = (
  aspectRatio: string,
  baseWidth: number
): { width: number; height: number } => {
  const [w, h] = aspectRatio.split(":").map(Number);

  if (!w || !h) {
    throw new Error(`Invalid aspect ratio: ${aspectRatio}`);
  }

  return {
    width: baseWidth,
    height: Math.round((baseWidth * h) / w),
  };
};

export const buildCloudflareImageUrl = (
  props: CloudflareImageLoaderProps
): string => {
  let baseSrc = props.src;

  if (!baseSrc) {
    return "";
  }

  if (isWpContentUrl(baseSrc) || isDatoContentUrl(baseSrc)) {
    baseSrc = storageUrlToLoaderUrl({ imageUrl: baseSrc });
  }

  const url = new URL(baseSrc);

  if (!isWpCloudflareUrl(url) && !isDatoCloudflareUrl(url)) {
    return url.toString();
  }

  let existingOptions: CloudflareImageLoaderProps = {};
  let imagePath = url.pathname;

  const cfMatch = imagePath.match(/^\/cdn-cgi\/image\/([^/]+)(\/.*)$/);

  if (cfMatch) {
    const [, existingTransforms, originalImagePath] = cfMatch;
    imagePath = originalImagePath;

    existingOptions = Object.fromEntries(
      existingTransforms
        .split(",")
        .map((param) => {
          const [key, ...valueParts] = param.split("=");
          return [key, valueParts.join("=")];
        })
        .filter(([key]) => key)
    );
  }

  // Cloudflare Image Resizing can't decode AVIF source images, so ask
  // DatoCMS's imgix-compatible API to re-encode as webp before Cloudflare
  // fetches and resizes it.
  if (isDatoCloudflareUrl(url) && imagePath.toLowerCase().endsWith(".avif")) {
    url.searchParams.set("fm", "webp");
  }

  const mergedOptions: CloudflareImageLoaderProps = {
    ...existingOptions,
    ...props,
  };

  // src should not be part of CF transformations
  delete (mergedOptions as any).src;

  if (mergedOptions.aspectRatio && mergedOptions.width) {
    const { width, height } = applyAspectRatio(
      mergedOptions.aspectRatio,
      Number(mergedOptions.width)
    );

    mergedOptions.width = width;
    mergedOptions.height = height;

    // aspectRatio should not be part of CF transformations
    delete (mergedOptions as any).aspectRatio;
  }

  const transformPath = Object.entries(mergedOptions)
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
    .map(([key, value]) => `${key}=${value}`)
    .join(",");

  return `${url.origin}/cdn-cgi/image/${transformPath}${imagePath}${url.search}`;
};

export const createCloudflareLoader =
  (options: CloudflareImageLoaderProps = {}) =>
  ({ src, width, quality }: ImageLoaderProps) => {
    return buildCloudflareImageUrl({
      ...options,
      src,
      ...(width ? { width: Number(width) } : {}),
      ...(quality ? { quality: Number(quality) } : {}),
    });
  };

export const imageLoader = createCloudflareLoader({
  format: "auto",
  fit: "cover",
  gravity: "auto",
});

// Used by next.config.js `images.loaderFile` as the global fallback loader
// for any `next/image` instance that doesn't pass its own `loader` prop.
export default imageLoader;
